import { FC } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, FileSignature, ShieldAlert } from 'lucide-react';
import { GroupContractWithMembersDto } from 'api/contract';
import { InputText } from 'components/forms/InputText';
import { SwitchComponent } from 'components/forms/Switch';
import { GenericLoading } from 'components/ui/Loader';
import { EmptyState, ErrorState } from 'components/ui/EmptyState';
import TableFooter from 'components/display/Table/TableFooter';
import { formatDay } from 'core/utils/dates';
import { BlockCard } from 'back-office/shared/components/BlockCard';
import { MemberRows } from './MemberRows';
import { PAGE_SIZE, useGroupMembersPayments } from './useGroupMembersPayments';

/**
 * Prélèvement des aliments, collectif par collectif.
 *
 * L'écran ne répond qu'à une question : qu'est-ce qui n'encaissera pas ? Un aliment sans
 * mandat est un contrat dont rien ne partira jamais, et un collectif en « le souscripteur
 * règle pour tous » qui n'a pas de mandat bloque tous ses aliments d'un coup. Ces deux
 * signaux passent donc avant les chiffres.
 */
export const GroupMembersPaymentsView: FC = () => {
  const {
    data,
    isLoading,
    error,
    reload,
    query,
    searchInput,
    setSearchInput,
    setBlockedOnly,
    setPageNumber,
  } = useGroupMembersPayments();

  const groups = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="w-full max-w-sm">
          <InputText
            name="search"
            label="Rechercher"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Identifiant d'un collectif ou d'un aliment"
          />
        </div>
        <div className="pb-2">
          <SwitchComponent
            name="blockedOnly"
            checked={query.blockedOnly}
            onChange={setBlockedOnly}
            label="Uniquement les collectifs ayant un aliment sans mandat"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-2xl bg-white py-10 shadow-xl shadow-zinc-900/10">
          <GenericLoading />
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : groups.length === 0 ? (
        <div className="rounded-2xl bg-white shadow-xl shadow-zinc-900/10">
          <EmptyState
            icon={<FileSignature className="h-8 w-8 text-slate-400" />}
            title={
              query.blockedOnly
                ? 'Aucun collectif bloqué'
                : 'Aucun contrat collectif ne correspond'
            }
            description={
              query.blockedOnly
                ? "Tous les aliments des collectifs portent un mandat : rien n'est bloqué à ce niveau."
                : undefined
            }
          />
        </div>
      ) : (
        groups.map((group) => <GroupCard key={group.id} group={group} />)
      )}

      <TableFooter
        currentPage={query.pageNumber}
        totalPages={Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
        totalItems={totalCount}
        pageSize={PAGE_SIZE}
        showInfo
        onPageChange={setPageNumber}
        pageSizeOptions={[PAGE_SIZE]}
      />
    </div>
  );
};

/** Un collectif et ses aliments. */
const GroupCard: FC<{ group: GroupContractWithMembersDto }> = ({ group }) => {
  // Le souscripteur règle pour tous, mais aucun mandat n'est rattaché au collectif :
  // le blocage est à la racine, tous les aliments en dépendent. Il prime sur le décompte
  // des aliments sans mandat, qui n'en est ici que la conséquence.
  const isRootBlocked = !group.billedByMembers && !group.hasGroupMandate;

  return (
    <BlockCard
      title={group.contractIdentifier}
      description={[
        group.typeLabel,
        group.subscriberName && `Souscripteur : ${group.subscriberName}`,
        group.startDate && `Effet le ${formatDay(group.startDate)}`,
      ]
        .filter(Boolean)
        .join(' · ')}
      icon={<FileSignature className="h-4 w-4" />}
      headerExtra={
        <div className="flex flex-wrap items-center gap-2">
          <span
            title={
              group.billedByMembers
                ? 'Chaque aliment porte son propre mandat.'
                : 'Le souscripteur du collectif règle pour tous ses aliments.'
            }
            className="rounded-full bg-white/15 px-2.5 py-0.5 text-[11px] font-semibold text-white"
          >
            {group.billedByMembers ? 'Chaque aliment règle' : 'Le souscripteur règle'}
          </span>
          <Link
            to={`/back-office/contrats-groupe/${group.id}`}
            className="rounded-lg bg-white/15 px-3 py-1 text-sm font-medium text-white transition-colors hover:bg-white/25"
          >
            Ouvrir
          </Link>
        </div>
      }
      flushBody
      isEmpty={group.members.length === 0}
      emptyLabel="Aucun aliment sur ce collectif"
    >
      <div className="px-5 pt-4">
        {isRootBlocked ? (
          <Banner
            tone="critical"
            icon={<ShieldAlert className="h-4 w-4" />}
            text={`Aucun mandat sur le collectif alors que le souscripteur règle pour tous : aucun des ${group.memberCount} aliments ne peut être prélevé.`}
          />
        ) : group.membersWithoutMandateCount > 0 ? (
          <Banner
            tone="warning"
            icon={<AlertTriangle className="h-4 w-4" />}
            text={`${group.membersWithoutMandateCount} aliment${
              group.membersWithoutMandateCount > 1 ? 's' : ''
            } sur ${group.memberCount} sans mandat : rien ne sera prélevé pour ${
              group.membersWithoutMandateCount > 1 ? 'ces contrats' : 'ce contrat'
            }.`}
          />
        ) : (
          <Banner
            tone="ok"
            icon={<CheckCircle2 className="h-4 w-4" />}
            text={`Les ${group.memberCount} aliments portent un mandat.`}
          />
        )}
      </div>

      <div className="mt-3">
        <MemberRows members={group.members} />
      </div>
    </BlockCard>
  );
};

const BANNER_TONE = {
  critical: 'bg-red-50 text-red-800',
  warning: 'bg-orange-50 text-orange-800',
  ok: 'bg-green-50 text-green-800',
} as const;

const Banner: FC<{
  tone: keyof typeof BANNER_TONE;
  icon: JSX.Element;
  text: string;
}> = ({ tone, icon, text }) => (
  <p
    className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium ${BANNER_TONE[tone]}`}
  >
    <span className="flex-shrink-0">{icon}</span>
    {text}
  </p>
);

export default GroupMembersPaymentsView;
